import {html, LitElement} from 'lit'
import {customElement, state, property} from 'lit/decorators.js'
import styles from './widget.scss'
import {Theme, Tile} from "../../themes/models";
import {ThemeObserver} from "../../themes/observer";
import {Fader} from "../fader/fader";
import {getTranslationService, ITranslationService, Translator} from "../../services/translation/translation-service";
import {getCustomerGroupService, ICustomerGroupService} from "../../services/group/customer-group";
import {CustomerService, ICustomerService} from "../../services/customer/customer-service";
import {getCountryService, ICountryService} from "../../services/country/country-service";
import {Customer} from "../../services/customer/customer";

@customElement('customer-widget')
export class CustomerWidget extends LitElement {

  @state() customerService: ICustomerService;
  @state() currentCustomer: Customer = null;
  @state() isLoggedIn: boolean = !!this.currentCustomer;
  @state() menuVisible: boolean = false;
  @state() hasError: boolean = false;
  @state() isEmailValid: boolean = true;
  @state() email: string = '';
  @state() password: string = '';
  @state() translator: Translator;
  @state() currentTheme: Theme = Theme.DARK;
  @state() cgs: ICustomerGroupService;
  @state() translationService: ITranslationService;
  @state() countryService: ICountryService;
  @state() saveLogin: boolean = false;
  @property() countryCode: string;

  constructor() {
    super();
    this.customerService = CustomerService.get();
    this.cgs = getCustomerGroupService();
    this.translationService = getTranslationService();
    this.countryService = getCountryService();

    this.onCustomerSubscription = this.onCustomerSubscription.bind(this);
    this.onMenuOpened = this.onMenuOpened.bind(this);
    this.onRequestThemeChange = this.onRequestThemeChange.bind(this);
    this.handleCustomLikesEvent = this.handleCustomLikesEvent.bind(this);

    window.addEventListener('ny-request-theme-change', this.onRequestThemeChange);

    this.customerService.subscribeForCustomer('NY-CUSTOMER-WIDGET', this.onCustomerSubscription)
  }

  async connectedCallback() {
    this.translator = await this.translationService.initTranslations('208671', this.countryService.getCountryCodeFromLocation());
    this.countryCode = this.getAttribute('countryCode') || '';
    window.addEventListener('ny-menu-opened', this.onMenuOpened);
    super.connectedCallback();
  }

  disconnectedCallback() {
    window.removeEventListener('ny-menu-opened', this.onMenuOpened);
    window.removeEventListener('ny-request-theme-change', this.onRequestThemeChange);
    super.disconnectedCallback();
  }

  onMenuOpened(event) {
    const menu = event.detail.menu;
    if (!(menu instanceof CustomerWidget) && this.menuVisible) {
      this.closeMenu();
    }
  }

  onRequestThemeChange(event: CustomEvent) {
    if (event.detail.caller instanceof ThemeObserver || event.detail.caller instanceof CustomerWidget || event.detail.caller instanceof Fader) {
      this.currentTheme = event.detail.theme as Theme;

      if (event.detail.caller instanceof ThemeObserver) {
        if (event.detail.tile === Tile.LEFT) {
          this.currentTheme = Theme.DARK;
        }
      }
      document.querySelector('.separator').setAttribute('class', `separator ${this.currentTheme}`);
    }
  }

  onCustomerSubscription(c: Customer) {
    this.currentCustomer = c;
    this.isLoggedIn = !!this.currentCustomer;
  }

  onWidgetClick() {
    if (!this.menuVisible) {
      window.dispatchEvent(new CustomEvent("ny-menu-opened", {detail: {menu: this as CustomerWidget}}));
    }
    this.menuVisible = !this.menuVisible;
    this.toggleScroll();
  }

  handleCustomLikesEvent() {
    window.dispatchEvent(new CustomEvent("ny-products-client", {detail: {route: 'FAVORITES'}}));
    this.closeMenu();
  }

  toggleScroll() {
    if (this.menuVisible) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'initial';
    }
  }

  onEmailChanged(e) {
    this.email = e.target.value;
  }

  performEmailValidation = () => {
    const isValid: boolean = this.email ? this.getEmailValidationLogic() : true;
    this.isEmailValid = isValid;
  }

  getEmailValidationLogic = () => {
    if(!this.email) return false;
    const regex = new RegExp('^([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})$');
    return regex.test(this.email.toString());
  }

  onPasswordChanged(e) {
    this.password = e.target.value;
  }

  onSaveLoginChanged(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target && target.type === "checkbox") {
      this.saveLogin = target.checked;
    }
  }

  resetForm() {
    this.email = '';
    this.password = '';
  }

  handleKeyPress(e: KeyboardEvent) {
    if (e && e.code.toLowerCase() === "escape") {
      e.preventDefault();
      this.closeMenu();
    }
  }

  async handleEnterPressed(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      await this.login(this.email, this.password, this.saveLogin)
    }
  }

  async login(email: string, password: string, permanent: boolean) {
    this.hasError = false;
    try {
      await this.customerService.login(this.email, this.password, permanent);
      this.closeMenu();
      this.resetForm();
    } catch (e) {
      this.hasError = true;
    }
  }

  async logout() {
    this.hasError = false;
    try {
      await this.customerService.logout();
      this.closeMenu();
      this.resetForm();
    } catch (e) {
      this.hasError = true;
    }
  }

  closeMenu() {
    this.menuVisible = false;
    this.toggleScroll();
  }

  render() {
    if (this.isLoggedIn) {
      return html`
        <style>${styles}</style>
        ${this.menuVisible ? html`
          <div class="closable-overlay" @click="${this.closeMenu}"></div>` : null}
        <button aria-label="toggle customer widget" name="toggle customer widget" class="customer-widget"
                @click="${this.onWidgetClick}"
                @keydown="${(e: KeyboardEvent) => this.handleKeyPress(e)}">
          ${this.menuVisible ? html`
            <svg class="grey">
              <use xlink:href="/img/icons/sprites.svg#times-circle"/>
            </svg>` : html`
            <svg class="red">
              <use xlink:href="/img/icons/sprites.svg#user-filled"/>
            </svg>`}
        </button>
        <div class=" customer-menu ${this.menuVisible ? 'active' : ''}"
             @keydown="${(e: KeyboardEvent) => this.handleKeyPress(e)}">
          <div class="email-spacer">
            <span>${this.currentCustomer.email}</span>
          </div>

          <nav tabindex="0" aria-label="Customer menu" class="menu-items">
            <a tabindex="0" href="${this.countryCode}/customer-profile/#/private/customer-profile/change-user-data">
              <img src="/img/icons/menu-profile.svg" alt="${this.translator.translate('widget.menu.profile')}"/>
              <span>${this.translator.translate('widget.menu.profile')}</span>
            </a>

            ${this.countryService.getCountryCodeFromLocation() === 'de' ? html`
              <a tabindex="0" href="${this.countryCode}/get-more-club/">
                <img src="/img/icons/menu-get-more.svg"
                     alt="${this.translator.translate('widget.menu.get-more-club')}"/>
                <span>${this.translator.translate('widget.menu.get-more-club')}</span>
              </a>` : null}

            <a tabindex="0" href="${this.countryCode}/products/?gender=${this.cgs.getCustomerGroup()}&likes=like"
               @click="${this.handleCustomLikesEvent}">
              <img src="/img/icons/menu-favorites.svg" alt="${this.translator.translate('widget.menu.favorites')}"/>
              <span>${this.translator.translate('widget.menu.favorites')}</span>
            </a>

            <button tabindex="0" @click="${this.logout}">
              <img src="/img/icons/menu-logout.svg" alt="${this.translator.translate('widget.menu.logout')}"/>
              <span>${this.translator.translate('widget.menu.logout')}</span>
            </button>
          </nav>
        </div>
      `
    } else {
      return html`
        <style>${styles}</style>
        ${this.menuVisible ? html`
          <div class="closable-overlay" @click="${this.closeMenu}"></div>` : null}
        <button aria-label="toggle customer widget" name="toggle customer widget"
                @keydown="${(e: KeyboardEvent) => this.handleKeyPress(e)}" class="customer-widget" tabindex="0"
                @click="${this.onWidgetClick}">
          ${this.menuVisible ? html`
            <svg class="grey">
              <use xlink:href="/img/icons/sprites.svg#times-circle"/>
            </svg>` : html`
            <svg class="${this.currentTheme}">
              <use xlink:href="/img/icons/sprites.svg#user-circle"/>
            </svg>`}
        </button>
        <div @keydown="${(e: KeyboardEvent) => this.handleKeyPress(e)}"
             class="customer-menu no-login ${this.menuVisible ? 'active' : ''}">
          <div class="error-container ${this.hasError ? 'has-error' : ''}">
            <div class="error-icon">
              <img src="/img/icons/exclamation-circle.svg" alt="Error Icon"/>
            </div>
            <div class="error-text">
              ${this.translator.translate('widget.login.error')}
            </div>
          </div>

          <div class="email-input">
            <input tabindex="${this.menuVisible ? 0 : -1}" type="email" @focusout="${this.performEmailValidation}" value="${this.email}"
                   @input="${this.onEmailChanged}"
                   @keyup="${this.handleEnterPressed}"
                   placeholder="${this.translator.translate('widget.login.placeholder.email')}">

            <div class="error ${!this.isEmailValid ? 'show' : ''}">
              <svg class="error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248
                248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418
                136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982
                11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"/>
              </svg>
              <span class="error-info">${this.translator.translate('widget.login.warning.valid.email')}</span>
            </div>
          </div>
          <div class="password-input">
            <input tabindex="${this.menuVisible ? 0 : -1}" type="password" value="${this.password}"
                   @input="${this.onPasswordChanged}"
                   @keyup="${this.handleEnterPressed}"
                   placeholder="${this.translator.translate('widget.login.placeholder.password')}">
          </div>

          <div class="stay-logged-reset-password-container">
            <div class="save-login">
              <input @click="${this.onSaveLoginChanged}" @keydown="${this.onSaveLoginChanged}"
                     id="customer-widget-checkbox" name="customer-widget-checkbox" type="checkbox"
                     tabindex="${this.menuVisible ? 0 : -1}"
                     aria-labelledby="save-login-label"/>
              <label id="save-login-label" aria-hidden="true" class="customer-widget-checkbox-label"
                     for="customer-widget-checkbox"
              >
                ${this.translator.translate('widget.login.save-login')}
              </label>
            </div>

            <div class="password-reset">
              <a tabindex="${this.menuVisible ? 0 : -1}"
                 href="${this.countryCode}/customer-profile/#/public/customer-profile/reset-password/${this.email && `?email=${this.email}`}">${this.translator.translate('widget.login.button.password-reset')}</a>
            </div>
          </div>
          <div class="buttons">
            ${this.countryService.getCountryCodeFromLocation() === 'de' ?
              html`<a tabindex="${this.menuVisible ? 0 : -1}" class="button-register"
                      href="/get-more-club/#/register">${this.translator.translate('widget.login.button.register')}</a>` :
              html`<a tabindex="${this.menuVisible ? 0 : -1}" class="button-register"
                      href="${this.countryCode}/customer-profile/#/public/customer-profile/register-user/customer-profile/noData">${this.translator.translate('widget.login.button.register')}</a>`
            }
            <button tabindex="${this.menuVisible ? 0 : -1}" class="button-login ${!(this.getEmailValidationLogic() && this.password) ? 'disabled' : ''}"
                    @click="${() => this.login(this.email, this.password, this.saveLogin)}">
              ${this.translator.translate('widget.login.button.login')}
            </button>
          </div>
        </div>
      `
    }
  }
}
